import { sensitiveWord } from '@/service/api/domain/domainSearch'
import { generateSafeRedirectUrl } from '@/service/api/login'

/**
 * Transform record to option
 *
 *
 * @param record
 */
export function transformRecordToOption<T extends Record<string, string>>(record: T) {
  return Object.entries(record).map(([value, label]) => ({
    value,
    label,
  })) as App.CommonType.Option<keyof T>[]
}

/**
 * Translate options
 *
 * @param options
 */
export function translateOptions(options: App.CommonType.Option<string>[]) {
  return options.map(option => ({
    ...option,
    label: option.label,
  }))
}

/**
 * Toggle html class
 *
 * @param className
 */
export function toggleHtmlClass(className: string) {
  function add() {
    document.documentElement.classList.add(className)
  }

  function remove() {
    document.documentElement.classList.remove(className)
  }

  return {
    add,
    remove,
  }
}

export function getDefaultValues<T extends Record<string, any>>(TpeyApp: new () => T): T {
  const instance = new TpeyApp()
  const defaultValues: any = {}

  for (const key in instance) {
    if (Object.prototype.hasOwnProperty.call(instance, key)) {
      const value = instance[key]
      if (typeof value === 'string') {
        defaultValues[key] = ''
      }
      else if (typeof value === 'number') {
        defaultValues[key] = 0
      }
      else if (typeof value === 'boolean') {
        defaultValues[key] = false
      }
      else {
        defaultValues[key] = undefined
      }
    }
  }

  return defaultValues as T
}

/**
 * 获取浏览器类型
 */
export function detectBrowser(ua: any) {
  if (/Edg\//i.test(ua))
    return 'Edge'
  if (/Chrome\//i.test(ua)) {
    // 检查是否为360浏览器
    if (ua.includes('360EE'))
      return '360极速浏览器'
    if (ua.includes('360SE'))
      return '360安全浏览器'
    return 'Chrome'
  }
  if (/Firefox\//i.test(ua))
    return 'Firefox'
  if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua))
    return 'Safari' // Safari without Chrome
  if (/MSIE|Trident/i.test(ua))
    return 'IE'
  if (/OPR\//i.test(ua))
    return 'Opera'
  if (/SamsungBrowser/i.test(ua))
    return '三星浏览器'
  if (/UCBrowser/i.test(ua))
    return 'UC浏览器'
  if (/MiuiBrowser/i.test(ua))
    return '小米浏览器'
  if (/MQQBrowser/i.test(ua))
    return 'QQ浏览器'
  if (/MicroMessenger/i.test(ua))
    return '微信内置浏览器'

  // 更多特殊浏览器检测可以在这里添加
  // 例如，针对360浏览器的更多特征检测

  // 如果以上都没有匹配，则返回未知浏览器
  return '未知浏览器'
}

/**
 * 格式化时间 年-月-日 时:分:秒
 */
export function formatStandardTime(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

export async function CheckKeyCompliance(key: any) {
  const res = await sensitiveWord(key)
  if (res.data) {
    window.$message.warning('含有不符合相关法律法规的内容，请更换关键词')
  }
  return res.data ?? false
}

export function getDeviceInfo() {
  const ua = navigator.userAgent
  const platform = navigator.platform

  // 初始化结果
  let device_type = 'unknown'
  let system = 'unknown'

  // 1. 判断设备类型 (桌面端 vs 移动端)
  // 注意：部分平板（如iPad Pro）在某些模式下可能被识别为桌面端，这里采用通用逻辑
  const isMobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i
  const isTabletRegex = /iPad|Android(?!.*Mobile)/i // 简单的平板判断，Android不带Mobile通常指平板

  if (isTabletRegex.test(ua)) {
    // 视需求而定，平板有时被归类为移动端，有时单独分类。
    // 根据你的需求，这里将平板归为移动端/手机H5范畴，或者你可以单独处理
    device_type = 'mobile'
  }
  else if (isMobileRegex.test(ua)) {
    device_type = 'mobile'
  }
  else {
    device_type = 'desktop'
  }

  // 2. 判断操作系统 (System)

  // --- 桌面端系统 ---
  if (device_type === 'desktop') {
    if (/Win/.test(platform) || /Windows NT/.test(ua)) {
      system = 'Windows'
    }
    else if (/Mac/.test(platform) || /Mac OS X/.test(ua)) {
      // 排除 iOS (iPad/iPhone 在旧版 UA 中可能包含 Mac，但新版 iPadOS 也是 Mac 内核)
      // 如果上面已经判定为 mobile，这里不会执行。
      // 针对新版 iPad (UserAgent 显示 Macintosh)，需要额外排除触控特征
      if (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(platform)) {
        // 这通常是 iPad，但既然 device_type 已经是 desktop (因为没命中 mobile 正则)，
        // 这里取决于你的业务定义。如果要把 iPad 算作 Mobile，需要在上面调整逻辑。
        // 此处严格按你的需求：如果是桌面端环境，则显示 MAC
        system = 'MAC'
      }
      else {
        system = 'MAC'
      }
    }
    else if (/CrOS/.test(ua)) {
      system = 'ChromeOS'
    }
    else if (/Linux/.test(platform) || /Linux/.test(ua)) {
      system = 'Linux' // 其他 Linux 发行版
    }
  }

  // --- 移动端系统 ---
  else if (device_type === 'mobile') {
    if (/Android/.test(ua)) {
      // 区分 Android 和 HarmonyOS (鸿蒙)
      // 鸿蒙系统的 UA 中通常包含 "HarmonyOS" 或 "HMOS"，但也兼容 Android 标识
      if (/HarmonyOS|HMOS/.test(ua)) {
        system = 'HarmonyOS'
      }
      else {
        system = 'Android'
      }
    }
    else if (/iPhone|iPad|iPod/.test(ua)) {
      system = 'IOS'
    }
    else if (/HarmonyOS|HMOS/.test(ua)) {
      // 防止某些非 Android 内核的鸿蒙设备漏网
      system = 'HarmonyOS'
    }
    else if (/BlackBerry/.test(ua)) {
      system = 'BlackBerry'
    }
    else {
      // 其他未知移动系统
      system = 'Other Mobile'
    }
  }

  return {
    device_type, // 'desktop' | 'mobile'
    system, // 'Windows', 'MAC', 'ChromeOS', 'Android', 'IOS', 'HarmonyOS'
    userAgent: ua,
  }
}

export async function generateSafeRedirectUrlFn(url: string) {
  const { data, code } = await generateSafeRedirectUrl(url)
  if (code === 1000) {
    window.open(data, '_blank')
  }
}
